import React, { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faShieldAlt,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons";
import "../../styles/common/StripePaymentForm.css";
import btnStyles from "../../styles/components/Button.module.css";

interface StripePaymentFormProps {
  onSuccess: (paymentIntentId: string) => void;
  onError: (message: string) => void;
  amount: number;
  currency: string;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  onSuccess,
  onError,
  amount,
  currency,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    // Required: Trigger form validation and submission
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(
        submitError.message || "Por favor revisa los datos de la tarjeta.",
      );
      setIsProcessing(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          // Optional: You can pre-fill billing details here if available
        },
      },
    });

    if (error) {
      setErrorMessage(
        error.message || "Ocurrió un error inesperado al procesar la tarjeta.",
      );
      onError(error.message || "Error en validación");
      setIsProcessing(false);
    } else if (paymentMethod) {
      // Send the PaymentMethod ID (token) to the parent
      await onSuccess(paymentMethod.id);
      // If onSuccess returns, it means we didn't redirect (e.g. user cancelled confirmation or error occurred parent-side)
      setIsProcessing(false);
    }
  };

  return (
    <form
      id="payment-form"
      onSubmit={handleSubmit}
      className="stripe-form-container fade-in"
    >
      <div className="stripe-header">
        <h6>
          <FontAwesomeIcon icon={faCreditCard} /> Pago seguro con Stripe
        </h6>
      </div>

      <div className="payment-element-container">
        <PaymentElement id="payment-element" options={{ layout: "tabs" }} />
      </div>

      {errorMessage && (
        <div className="stripe-error-message fade-in">{errorMessage}</div>
      )}

      <div className="stripe-security-badge">
        <FontAwesomeIcon icon={faShieldAlt} />
        <span>Pago encriptado y seguro a través de Stripe</span>
      </div>

      <button
        disabled={isProcessing || !stripe || !elements}
        id="submit"
        className={`${btnStyles.primary} ${btnStyles.fullWidth} stripe-submit-btn`}
      >
        {isProcessing ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin /> Procesando...
          </>
        ) : (
          `Pagar ${amount.toFixed(2)} ${currency}`
        )}
      </button>
    </form>
  );
};

export default StripePaymentForm;
